<template>
  <button

    :data-type="type"
    class="relative inline-flex gap-1 min-w-6 min-h-6  items-center font-bold justify-center rounded-md max-h-11 transition-colors duration-200  select-none box-border overflow-hidden"
    :class="[
      buttonSizeClasses,
      buttonColourClasses,
      disabled ? 'cursor-not-allowed' : loading ? 'cursor-wait' : 'cursor-pointer',
      type,
    ]"
    :disabled="disabled"
    :data-test-id="generateDataTestId(computedDataTestId, 'button')"
    :aria-label="ariaLabel || text"
    :aria-busy="loading || undefined"
    :aria-disabled="loading || undefined"
    @click.stop="handleClick"
  >
    <!-- Loading spinner, overlaid so the button keeps its width while busy -->
    <span
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center"
      data-test-id="button-loading"
    >
      <AM3Loading
        size="sm"
        :color="loadingSpinnerColor"
      />
    </span>

    <!-- Content is hidden (not removed) while loading to preserve button dimensions -->
    <span
      class="inline-flex gap-1 items-center justify-center h-full"
      :class="loading && 'invisible'"
    >
      <!-- Left icon if provided -->
      <AM3Icon
        v-if="iconLeft"
        :key="`left-${iconLeft}`"
        :type="iconLeft"
        :size="iconSize"
        color="currentColor"
      />

      <!-- Left separator if enabled and icon is present -->
      <div
        v-if="leftSeparator && iconLeft"
        class="separator mx-2 w-px h-[60%] opacity-40 bg-current"
      />

      <!-- Button text -->
      <span
        v-if="text"
        :class="iconSize !== 'xs' || 'px-1' "
        class=" text-nowrap"
      >{{ text }}</span>

      <!-- Right separator if enabled and icon is present -->
      <div
        v-if="rightSeparator && iconRight"
        class="separator mx-2 w-px h-[60%] opacity-40 bg-current"
      />

      <!-- Right icon if provided -->
      <AM3Icon
        v-if="iconRight"
        :key="`right-${iconRight}`"
        :type="iconRight"
        :size="iconSize"
        color="currentColor"
      />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { IconSize } from './icon/AM3Icon.vue'
import AM3Icon from './icon/AM3Icon.vue'
import AM3Loading from './AM3Loading.vue'
import '@/components/am3-generic/core/theme.css'
import type { iconList } from './icon/svgs/_iconList'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

export type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'link' | 'gradient' | 'ghost' | 'glass'
export type ButtonSize = 'md' | 'sm' | 'xs'
export type IconType = typeof iconList[number] | ''

interface Props {
  // Button text content
  text?: string
  // Button type/style
  type?: ButtonType
  // Button size
  size?: ButtonSize
  // Disabled state
  disabled?: boolean
  // Busy state: shows a spinner, keeps the button's size and ignores clicks
  loading?: boolean
  // Left icon type (if any)
  iconLeft?: IconType
  // Right icon type (if any)
  iconRight?: IconType
  // Show separator between left icon and text
  leftSeparator?: boolean
  // Show separator between text and right icon
  rightSeparator?: boolean
  // Data test id prefix
  dataTestId?: string
  // Aria label attribute
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  iconLeft: '',
  iconRight: '',
  leftSeparator: false,
  rightSeparator: false,
  dataTestId: undefined,
  ariaLabel: null,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

// Determine icon size based on button size
const iconSize = computed((): IconSize => {
  if (props.size === 'xs') return 'sm'
  return 'sm'
})

// Compute button classes based on size
const buttonSizeClasses = computed(() => {
  if (props.type === 'link') {
    const base = 'text-brand hover:text-brand-dark hover:underline focus:underline active:outline-none focus:outline-none'
    const size = props.size === 'xs' ? 'text-xs' : props.size === 'sm' ? 'text-sm' : ''
    return base + ' ' + size
  }

  switch (props.size) {
    case 'xs':
      return 'h-6 px-2 py-1 text-xs'
    case 'sm':
      return 'h-9 px-3 py-1.5 text-sm'
    default:
      return 'h-11 px-4 py-2 text-sm'
  }
})

// Compute button colours
const buttonColourClasses = computed(() => {
  if (props.type === 'link') {
    return 'hover:text-brand-dark disabled:text-disabled text-brand focus:outline-transparent focus:outline-offset-1'
  }

  const base = 'border border-solid outline outline-1  focus:outline-offset-2  '

  switch (props.type) {
    case 'primary':
      return (
        base
        + 'bg-brand border-brand disabled:outline-strong disabled:border-disabled  disabled:bg-disabled hover:bg-brand-dark focus:outline-brand text-inverse'
      )
    case 'secondary':
      return (
        base
        + 'bg-surface-light border-brand disabled:text-stone-400 disabled:border-strong disabled:bg-[var(--buttons-secondary-disabled)]  hover:bg-brand-light focus:outline-surface-dark text-brand'
      )
    case 'tertiary':
      return (
        base
        + 'bg-surface-light border-surface-dark disabled:text-disabled disabled:bg-stone-100 hover:bg-surface focus:outline-surface-dark text-primary'
      )
    case 'danger':
      return 'bg-danger disabled:bg-danger/50 disabled:text-primary hover:bg-[var(--font-colour-danger)] focus:outline-danger text-inverse'
    case 'gradient':
      return (
        base
        + 'enabled:bg-gradient-to-r from-brand to-brand-dark border-transparent disabled:bg-disabled hover:from-brand-dark hover:to-brand focus:outline-brand text-inverse'
      )
    case 'glass':
      return (
        base
        + 'bg-white/15 hover:bg-white/30 duration-75 focus:outline-brand border-none backdrop-blur-sm text-inverse'
      )
    default:
      return ''
  }
})

// Spinner colour needs to contrast with the button background
const loadingSpinnerColor = computed(() =>
  ['primary', 'danger', 'gradient', 'glass'].includes(props.type) ? 'white' : 'brand',
)

// Handle click event
const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}

// Compute dataTestId fallback
const computedDataTestId = computed(() => props.dataTestId || props.text || '')
</script>
